<template>
  <Toast />
  <ConfirmDialog group="headless">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col items-center p-8 bg-surface-0 dark:bg-surface-900 rounded">
        <div
          class="rounded-full bg-white outline outline-black inline-flex justify-center items-center h-24 w-24 -mt-20">
          <i class="pi pi-question text-5xl"></i>
        </div>
        <span class="font-bold text-2xl block mb-2 mt-6">{{ message.header }}</span>
        <p class="mb-0">{{ message.message }}</p>
        <div class="flex items-center gap-2 mt-6">
          <Button :label="message.rejectLabel" outlined @click="rejectCallback" class="w-32"></Button>
          <Button :label="message.acceptLabel" @click="acceptCallback" class="w-32"></Button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
  <ModalTable v-if="modalShowTable" @closeModal="closeModal" :table="selectedTable" />
  <IndexView />
  <div id="overlay" class="fixed inset-0 bg-black bg-opacity-30 z-30" :class="{ hidden: !isBlur }"></div>
  <ViewContainer>
    <section class="p-6">
      <PageTitle title="Stok Opname" />
      <div class="border-t-2 border-primary bg-white dark:bg-dark-900 w-full rounded-lg py-6 px-5 shadow-lg shadow-light-100 dark:shadow-dark-500">
        <div id="table-header" class="mb-5 flex justify-between">
          <AddButton v-if="role_id !== 'null'" to_url="#" button_text="+ Tambah Stok Opname" button_class="text-base py-2 font-medium"
            @click="showModal" />
        </div>
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full align-middle">
            <div class="overflow-hidden shadow-lg rounded-lg">
              <TableComponent>
                <TableHead>
                  <TableRow>
                    <TableHeadCell>No</TableHeadCell>
                    <TableHeadCell>Pembuat</TableHeadCell>
                    <TableHeadCell>Tanggal</TableHeadCell>
                    <TableHeadCell>Tipe</TableHeadCell>
                    <TableHeadCell>Status</TableHeadCell>
                    <TableHeadCell>Action</TableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow v-if="allStockOpnames.length == 0">
                    <TableCell colspan="6" customClass="text-center italic text-gray-400 dark:text-gray-500"
                      noTextColor>
                      Belum ada data
                    </TableCell>
                  </TableRow>
                  <TableRow v-else v-for="(stockOpname, index) in allStockOpnames" :key="stockOpname.id" :index="index" isBody>
                    <TableCell>{{ index + 1 }}</TableCell>
                    <TableCell>{{ userNames[stockOpname.create_by] || 'Loading...' }}</TableCell>
                    <TableCell>{{ formatDate(stockOpname.createdAt) }}</TableCell>
                    <TableCell>{{ stockOpname.form_type == 'common' ? 'Stock Opname' : 'Lain - lain' }}</TableCell>
                    <TableCell>
                      <span :class="{
                        'bg-success dark:bg-success text-light-50 dark:text-white': stockOpname.status === 'paid',
                        'bg-info dark:bg-info text-white dark:text-white': stockOpname.status === 'pending',
                        'bg-danger dark:bg-danger text-light-50 dark:text-white': stockOpname.status === 'canceled'
                      }" class="px-2 py-1 rounded-full text-sm font-medium">
                        {{ getStatusText(stockOpname.status) }}
                      </span>
                    </TableCell>
                    <TableCell noTextColor customClass="text-gray-500 dark:text-gray-400 flex">
                      <button :id="'view-data-' + stockOpname.id" @click="viewStockOpname(stockOpname)" class="bg-info hover:bg-hover-info flex justify-center items-center mx-1 p-2 rounded-md group">
                        <EyeIcon class="h-4 w-4 text-white group-hover:text-white" />
                      </button>
                      <template v-if="stockOpname.status != 'paid'">
                        <button :id="'edit-data-' + stockOpname.id" @click="editStockOpname(stockOpname)"
                          class="bg-warning hover:bg-hover-warning flex justify-center items-center mx-1 p-2 rounded-md group">
                          <PencilSquareIcon class="h-4 w-4 text-light-900 group-hover:text-primary" />
                        </button>
                        <button :id="'delete-data-' + stockOpname.id" @click="deleteStockOpname(stockOpname)"
                          class="bg-danger hover:bg-hover-danger flex justify-center items-center mx-1 p-2 rounded-md group">
                          <TrashIcon class="h-4 w-4 text-white group-hover:text-white" />
                        </button>
                      </template>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </TableComponent>
              <PaginationButtons v-model="currentPage" :totalPages="totalPages" />
            </div>
          </div>
        </div>
      </div>
    </section>
  </ViewContainer>
</template>

<script>
import IndexView from "@/views/IndexView.vue";
import PageTitle from "@/components/PageTitle.vue";
import AddButton from "@/components/buttons/AddButton.vue";
import ModalTable from "@/components/modals/ModalTable.vue";
import ViewContainer from "@/components/ViewContainer.vue";
import {
  TableComponent,
  TableHead,
  TableRow,
  TableHeadCell,
  TableBody,
  TableCell,
} from "@/components/table";
import { PencilSquareIcon, TrashIcon, EyeIcon } from "@heroicons/vue/24/solid";
import { mapGetters, mapActions } from "vuex";
import PaginationButtons from "@/components/PaginationButtons.vue";
import { Button, ConfirmDialog, Toast } from "primevue";

export default {
  name: "StockOpnameView",
  components: {
    IndexView, PageTitle, AddButton, ModalTable, ViewContainer, TableComponent, TableHead, TableRow, TableHeadCell, TableBody, TableCell, PencilSquareIcon, TrashIcon, EyeIcon, PaginationButtons, Toast, ConfirmDialog, Button
  },
  data() {
    return {
      role_id: localStorage.getItem("role_id"),
      modalShowTable: false,
      isBlur: false,
      selectedTable: null,
      currentPage: 1,
      totalPages: 1,
      userNames: {},
    }
  },
  computed: {
    ...mapGetters('stock_opnames', ['allStockOpnames'])
  },
  created() {
    this.fetchStockOpnames();
  },
  watch: {
    currentPage: {
      immediate: true,
      handler(newVal) {
        this.fetchStockOpnames({ page: newVal });
      },
    },
    allStockOpnames: {
      immediate: true,
      async handler(newVal) {
        if (!newVal || !newVal.length) return;
        
        for (const opname of newVal) {
          if (opname.create_by && !this.userNames[opname.create_by]) {
            try {
              const response = await this.$store.dispatch('users/fetchUserById', opname.create_by);
              if (response?.data) {
                // Store the user's name, username, or email (prioritize in this order)
                const displayName = response.data.name || response.data.username || response.data.email || 'Unknown User';
                // Use object spread to trigger reactivity
                this.userNames = {
                  ...this.userNames,
                  [opname.create_by]: displayName
                };
              }
            } catch (error) {
              console.error('Error fetching user:', error);
              this.userNames = {
                ...this.userNames,
                [opname.create_by]: 'Unknown User'
              };
            }
          }
        }
      },
    },
  },
  methods: {
    ...mapActions('stock_opnames', ['deleteStockOpnameById']),
    formatDate(date) {
      console.log(date);
      if (!date) return "-";
      const d = new Date(date);
      const pad = (num) => num.toString().padStart(2, "0");

      const day = pad(d.getDate());
      const month = pad(d.getMonth() + 1);
      const year = d.getFullYear().toString().slice(-2);
      const hours = pad(d.getHours());
      const minutes = pad(d.getMinutes());
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    },
    async fetchStockOpnames(params = {}) {
      if (!params.page) params.page = this.currentPage;

      try {
        const { pagination } = await this.$store.dispatch('stock_opnames/fetchStockOpnames', params);
        if (pagination) {
          this.totalPages = pagination.totalPages;
        }
      } catch (error) {
        console.log(error);
      }
    },
    getStatusText(status) {
      const statusMap = {
        'paid': 'Posted',
        'pending': 'Draft',
        'canceled': 'Void'
      };
      return statusMap[status] || status;
    },
    showModal() {
      this.$router.push('/stock-opname/new');
      // this.modalShowTable = true;
      // this.isBlur = true;
      // this.selectedTable = null;
    },
    closeModal() {
      setTimeout(() => {
        this.modalShowTable = false;
        this.isBlur = false;
        this.fetchTables();
      }, 200);
    },
    editStockOpname(data) {
      this.$router.push(`stock-opname/edit/${data.id}`);
    },
    viewStockOpname(data) {
      this.$router.push(`stock-opname/view/${data.id}`);
    },
    async deleteStockOpname(table) {
      this.$confirm.require({
        group: 'headless',
        header: 'Apakah anda yakin ingin menghapus stok opname?',
        message: 'Silahkan konfirmasi sebelum diproses.',
        acceptLabel: 'Ya',
        rejectLabel: 'Tidak',
        accept: async () => {
          try {
            await this.deleteStockOpnameById(table.id);
            this.fetchStockOpnames();
            this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Berhasil menghapus stok opname', life: 3000 });
          }
          catch (error) {
            console.error('Gagal menghapus stok opname', error);
            this.$toast.add({ severity: 'error', summary: 'Failed', detail: 'Gagal menghapus stok opname', life: 3000 });
          }
        },
        reject: () => {
          this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Batal menghapus stok opname', life: 3000 });
        }
      });
    }
  },
}
</script>
